import { useEffect, useState } from 'react';

import { QUERY_ROOT_ID, useCompleteFragment } from '@eluve/apollo-client';
import {
  Button,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  InputLabel,
  Label,
  Stack,
  Switch,
  Text,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@eluve/components';
import {
  useAppointmentId,
  useIsUserAppointmentOwner,
} from '@eluve/frontend-appointment-hooks';
import {
  AppointmentLanguageSelector,
  NewAppointmentTemplateSelector,
} from '@eluve/frontend-feature-appointment';
import { medicalNotesTemplatesFragment } from '@eluve/frontend-feature-user-settings';
import { LlmOutputTypesLookup } from '@eluve/graphql-types';
import { graphql } from '@eluve/graphql.tada';
import { useTranslation } from '@eluve/i18n';
import { useAssignedTenantIdFromParams } from '@eluve/session-helpers';
import {
  AppointmentUserMicSettings,
  FeatureFlaggedComponent,
  MicrophonePermissionProvider,
  TenantOrUserFeatureFlaggedComponent,
  useSelectedMicrophoneDevice,
} from '@eluve/smart-blocks';

import { useStartAppointmentRecording } from '../../machines/transcription/hooks/useStartAppointmentRecording';
import {
  useTranscriptionMachineActor,
  useTranscriptionMachineSelector,
} from '../../machines/transcription/hooks/useTranscriptionMachine';
import { UploadAudioFileButton } from '../UploadAudioFileButton';

const AppointmentTemplateFragment = graphql(`
  fragment AppointmentTemplate on Appointments {
    __typename
    id
    initialPromptTemplateId
    initialLlmOutputTemplateId
  }
`);

export interface NewAppointmentFormDefaultProps {
  isCompactMicrophone: boolean;
  isSettingsAvailable: boolean;
}

export const NewAppointmentFormDefault: React.FC<
  NewAppointmentFormDefaultProps
> = ({ isCompactMicrophone, isSettingsAvailable }) => {
  const { t } = useTranslation();
  const tenantId = useAssignedTenantIdFromParams();
  const appointmentId = useAppointmentId();

  const isAnyAppointmentInSession = useTranscriptionMachineSelector((state) => {
    return state.matches('InSession');
  });

  const isValidatingAudioCapture = useTranscriptionMachineSelector((state) =>
    state.matches('ValidatingAudioCapture'),
  );

  const audioRecordingMachine = useTranscriptionMachineActor();

  const hasValidAudioSource = useTranscriptionMachineSelector(
    (state) => state.context.hasValidAudioSource,
  );

  const [selectedMicId, setSelectedMicId] = useSelectedMicrophoneDevice();
  const [hasAudioStream, setHasAudioStream] = useState(false);
  const [playDisclaimer, setPlayDisclaimer] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(isSettingsAvailable);

  const { isStartingAppointmentRecording, startAppointmentRecording } =
    useStartAppointmentRecording({
      tenantId,
      appointmentId,
      selectedMicId,
    });

  useEffect(
    function validateAudioCaptureOnMicChange() {
      audioRecordingMachine.send({
        type: 'AUDIO_VALIDATION_REQUESTED',
        deviceId: selectedMicId ?? undefined,
      });

      return () => {
        audioRecordingMachine.send({ type: 'CANCEL_VALIDATION' });
      };
    },
    [selectedMicId, audioRecordingMachine],
  );

  const isUserApptOwner = useIsUserAppointmentOwner();

  const appointmentData = useCompleteFragment({
    fragment: AppointmentTemplateFragment,
    key: {
      id: appointmentId,
    },
  });

  const medicalNotesTemplatesData = useCompleteFragment({
    fragment: medicalNotesTemplatesFragment,
    key: QUERY_ROOT_ID,
  });

  const selectedTemplateId = appointmentData?.initialPromptTemplateId;
  const templates = medicalNotesTemplatesData?.activePromptTemplates ?? [];
  const selectedTemplate = templates.find(
    (template) => template.id === selectedTemplateId,
  );

  const isTemplateSelected =
    selectedTemplate?.outputType === LlmOutputTypesLookup.DYNAMIC_OUTPUT
      ? Boolean(appointmentData?.initialLlmOutputTemplateId)
      : true;

  const canStartSession =
    Boolean(selectedMicId) &&
    isTemplateSelected &&
    isUserApptOwner &&
    hasAudioStream &&
    !isAnyAppointmentInSession &&
    hasValidAudioSource;

  const handleStartClick = () => {
    startAppointmentRecording({ playDisclaimer });
  };

  return (
    <Stack gap={6}>
      <Stack align="stretch">
        <Collapsible open={settingsOpen} onOpenChange={setSettingsOpen}>
          {isSettingsAvailable && (
            <Stack
              className="mb-5"
              align="stretch"
              justify="between"
              gap={2}
              direction="horizontal"
            >
              <Text inline weight="semibold">
                {t('appointmentPage.newAppointment.sessionSettings')}
              </Text>
              <CollapsibleTrigger asChild>
                <Button variant="outline" size="sm">
                  {settingsOpen
                    ? t('appointmentPage.newAppointment.viewLessOptions')
                    : t('appointmentPage.newAppointment.viewMoreOptions')}
                </Button>
              </CollapsibleTrigger>
            </Stack>
          )}

          <MicrophonePermissionProvider>
            <AppointmentUserMicSettings
              isCompact={isCompactMicrophone}
              selectedMicId={selectedMicId}
              setSelectedMicId={setSelectedMicId}
              setHasAudioStream={setHasAudioStream}
              hasError={hasValidAudioSource === false}
            />
          </MicrophonePermissionProvider>

          <CollapsibleContent
            className='h-0 data-[state="closed"]:overflow-hidden data-[state="open"]:h-auto data-[state="open"]:pt-5'
            data-state={settingsOpen ? 'open' : 'closed'}
            forceMount
          >
            <NewAppointmentTemplateSelector />

            <TenantOrUserFeatureFlaggedComponent flag="MULTILINGUAL_SUPPORT">
              <div className="mt-5">
                <AppointmentLanguageSelector inputLanguage outputLanguage />
              </div>
            </TenantOrUserFeatureFlaggedComponent>

            <FeatureFlaggedComponent flag="SHOW_PLAY_DISCLAIMER_OPTION">
              <InputLabel
                className="mt-5"
                label={t('appointmentPage.disclaimer')}
              >
                <Stack
                  className="rounded-md border border-border-primary bg-bg-primary px-3 py-2"
                  gap={3}
                  direction="horizontal"
                >
                  <Switch
                    id="play-disclaimer"
                    checked={playDisclaimer}
                    onCheckedChange={setPlayDisclaimer}
                  />
                  <Label
                    className="text-sm font-normal text-text-secondary"
                    htmlFor="play-disclaimer"
                  >
                    {t('appointmentPage.newAppointment.playDisclaimer')}
                  </Label>
                </Stack>
              </InputLabel>
            </FeatureFlaggedComponent>
          </CollapsibleContent>
        </Collapsible>
      </Stack>

      <Stack className="mt-2 mb-8" justify="end" gap={2} direction="horizontal">
        <FeatureFlaggedComponent flag="ALLOW_AUDIO_FILE_UPLOAD_FOR_APPOINTMENT">
          <UploadAudioFileButton appointmentId={appointmentId} />
        </FeatureFlaggedComponent>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              size="lg"
              className="flex-1"
              disabled={!canStartSession || isValidatingAudioCapture}
              data-accessibility-label="start-session-button"
              loading={isStartingAppointmentRecording}
              onClick={handleStartClick}
            >
              {t('appointmentPage.newAppointment.startSession')}
            </Button>
          </TooltipTrigger>
          {!selectedMicId && (
            <TooltipContent>
              {t('appointmentPage.newAppointment.selectMicrophoneTooltip')}
            </TooltipContent>
          )}

          {!isUserApptOwner && (
            <TooltipContent>
              {t('appointmentPage.newAppointment.ownerOnlyStartTooltip')}
            </TooltipContent>
          )}

          {!hasAudioStream && (
            <TooltipContent>
              {t('appointmentPage.newAppointment.micAccessTooltip')}
            </TooltipContent>
          )}

          {hasValidAudioSource === false && (
            <TooltipContent>
              {t('appointmentPage.newAppointment.noAudioTooltip')}
            </TooltipContent>
          )}

          {isAnyAppointmentInSession && (
            <TooltipContent>
              {t('appointmentPage.newAppointment.alreadyInSessionTooltip')}
            </TooltipContent>
          )}

          {isStartingAppointmentRecording && (
            <TooltipContent>
              {t('appointmentPage.newAppointment.sessionStartingTooltip')}
            </TooltipContent>
          )}
        </Tooltip>
      </Stack>
    </Stack>
  );
};
